import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { tournamentApi, Tournament } from '../services/api-client';
import { useAuthContext } from '../contexts/AuthContext';
import { MobileLayout } from '../components/layout/MobileLayout';
import { PageHeader } from '../components/layout/PageHeader';
import { PullToRefresh } from '../components/layout/PullToRefresh';
import AdBanner from '../components/AdBanner';
import { AD_SLOTS } from '../config/adSlots';
import { formatDate } from '../utils/dateTime';

export default function TournamentsPage() {
  const navigate = useNavigate();
  const { isAdmin } = useAuthContext();
  const [tournaments, setTournaments] = useState<Tournament[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [adRefreshKey, setAdRefreshKey] = useState(0);

  useEffect(() => {
    loadTournaments();
  }, []);

  const loadTournaments = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await tournamentApi.list();
      setTournaments(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load tournaments');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'upcoming':
        return 'bg-blue-100 text-blue-800';
      case 'in_progress':
        return 'bg-green-100 text-green-800';
      case 'completed':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const handleRefresh = async () => {
    await loadTournaments();
    setAdRefreshKey((prev) => prev + 1);
  };

  return (
    <MobileLayout>
      <PageHeader
        title="Tournaments"
        subtitle="Browse wrestling tournaments"
        actions={
          <>
            <button
              onClick={handleRefresh}
              disabled={loading}
              className="p-2 text-gray-300 hover:bg-gray-700 rounded-full transition-colors disabled:opacity-50"
              aria-label="Refresh"
            >
              <svg
                className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
            </button>
            {isAdmin && (
              <button
                onClick={() => navigate('/tournaments/create')}
                className="ml-2 px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg text-sm font-medium hover:shadow-lg transition-colors"
              >
                + Create
              </button>
            )}
          </>
        }
      />

      <PullToRefresh onRefresh={handleRefresh}>
        <div className="px-4 py-4">
          {/* Ad Banner - Top placement for visibility */}
          <div className="py-4 mb-4">
            <AdBanner slot={AD_SLOTS.tournaments} format="horizontal" refreshKey={adRefreshKey} />
          </div>

          {loading && tournaments.length === 0 ? (
            <div className="flex justify-center items-center py-12">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500 mx-auto"></div>
                <p className="mt-4 text-gray-600">Loading tournaments...</p>
              </div>
            </div>
          ) : error ? (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-red-800">{error}</p>
              <button
                onClick={loadTournaments}
                className="mt-2 text-red-600 hover:text-red-800 underline"
              >
                Try again
              </button>
            </div>
          ) : tournaments.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-600">No tournaments available yet</p>
            </div>
          ) : (
            <div className="space-y-3">
              {tournaments.map((tournament) => (
                <div
                  key={tournament.id}
                  onClick={() => navigate(`/tournaments/${tournament.id}`)}
                  className="bg-white rounded-lg shadow-sm p-4 cursor-pointer hover:shadow-md transition-shadow active:scale-[0.98] active:shadow-sm"
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-base font-bold text-gray-900 truncate">
                        {tournament.name}
                      </h3>
                      {tournament.location && (
                        <p className="text-sm text-gray-600 truncate">📍 {tournament.location}</p>
                      )}
                    </div>
                    <span
                      className={`ml-2 px-2 py-1 rounded-full text-xs font-medium flex-shrink-0 ${getStatusColor(tournament.status)}`}
                    >
                      {tournament.status.replace('_', ' ')}
                    </span>
                  </div>

                  <div className="flex items-center justify-between text-sm text-gray-600 mb-2">
                    <span>
                      {formatDate(tournament.startDate)}
                      {tournament.endDate && tournament.endDate !== tournament.startDate && (
                        <> - {formatDate(tournament.endDate)}</>
                      )}
                    </span>
                  </div>

                  <div className="flex items-center gap-4 text-sm">
                    <span className="text-gray-600">
                      {tournament.weightClasses.length} weight classes
                    </span>
                    <span className="text-gray-600">Budget: {tournament.defaultBudget}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </PullToRefresh>
    </MobileLayout>
  );
}
